/**
 * @fileoverview Environment detection utilities
 * @module utils/environment
 */

// The ONE tier classifier (DEV-TIER-RULE), through its generated ESM half: no tier name is compared by hand.
import { isLocalDevEnv } from '@puffin-core/env';

import { LOG_LEVEL } from '../constants.js';

/**
 * Check if running in a browser environment
 * @returns {boolean} True if in browser
 */
export function isBrowser() {
  return typeof window !== 'undefined';
}

/**
 * Check if running in development mode
 * @returns {boolean} True if in development
 */
export function isDevelopment() {
  // A DECLARED NODE_ENV decides first, and only `development` / `test` are local development: any
  // other declared value (`production`, `preview`, `staging`, a typo) is a deployed tier. Until
  // 2026-09-30 only exact `production` said so, and `preview` / `staging` fell through to hostname
  // guessing (SEC-B deployment-tier unit: ParcelPuffin's production runs `NODE_ENV=preview`).
  if (typeof process !== 'undefined' && process.env) {
    const nodeEnv = process.env.NODE_ENV;
    if (isLocalDevEnv({ NODE_ENV: nodeEnv })) {
      return true;
    }
    if (nodeEnv) {
      return false;
    }
  }

  // Browser-based detection
  if (isBrowser() && window.location) {
    const { hostname, port } = window.location;
    // localhost or 127.0.0.1 indicates development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return true;
    }
    // Non-empty port typically indicates development server
    if (port && port !== '' && port !== '80' && port !== '443') {
      return true;
    }
  }

  return false;
}

/**
 * Check if running in production mode
 * @returns {boolean} True if in production
 */
export function isProduction() {
  return !isDevelopment();
}

/**
 * Get log level from environment variables
 * @returns {string} Log level
 */
export function getLogLevelFromEnv() {
  // Check for explicit log level setting
  if (typeof process !== 'undefined' && process.env) {
    const envLogLevel = process.env.VITE_LOG_LEVEL || process.env.LOG_LEVEL;
    if (envLogLevel) {
      const upperLevel = envLogLevel.toUpperCase();
      // Validate the log level exists
      if (Object.values(LOG_LEVEL).includes(upperLevel)) {
        return upperLevel;
      }
      // Handle 'WARN' alias for 'WARNING' (LOG_LEVEL.WARN value is 'WARNING')
      if (upperLevel === 'WARN') {
        return LOG_LEVEL.WARN;
      }
    }
  }

  // Default based on environment
  if (isDevelopment()) {
    return LOG_LEVEL.INFO;
  }

  return LOG_LEVEL.WARN;
}
